import { createContext, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useScreen } from '../lib/layout.ts';
import { UnsavedBar } from '../lib/unsaved.tsx';
import { cn } from '../lib/utils.ts';
import { RecordSidebar } from './record-sidebar.tsx';
import { TabCard } from './ui/tab-card.tsx';
import { SEG_TRACK, TAB_COUNT_ACTIVE, TAB_COUNT_INACTIVE, TAB_TRACK, tabStateClass } from './ui/tab-style.ts';

/** Weiterer Reiter neben „Übersicht“ (z. B. „Positionen“ am Auftrag); `count` steht grau hinter dem Namen. */
export interface RecordTab {
  value: string;
  label: string;
  count?: number;
  content: ReactNode;
}

const OVERVIEW = 'overview';
const KENNZAHLEN = 'kennzahlen';
const VERLAUF = 'verlauf';

/** Verlauf (Chatter) eines Datensatzes: welcher Datensatz – die Seitenleiste baut daraus die Verlauf-Karte. */
export interface RecordHistory {
  entityType: string;
  entityId: string;
}

/**
 * Eigenschaften von `RecordLayout` (Ticket 1.312): Die Seitenleiste bekommt der Rahmen nur als **Beschreibung** – welche
 * Kennzahlen (`figures`), welcher KI-Hinweis (`hint`), welcher Verlauf (`history`), welche Vorschau (`preview`) – und baut
 * daraus auf jeder Datensatz-Art dieselbe Leiste (DESIGN „Rechte Seitenleiste“, Baustein-Katalog „Datensatz-Seitenleiste“).
 */
export interface RecordLayoutProps {
  /** Vollansicht (eigene Seite) oder Inhalt eines Seitenpanels. */
  variant?: 'page' | 'panel';
  /** Pfad und `RecordHeader` über dem Inhalt (volle Breite). */
  head?: ReactNode;
  /** Inhalt (Karten) der Übersicht. */
  overview: ReactNode;
  primaryAction?: ReactNode;
  /** Weitere Reiter nach „Übersicht“. */
  tabs?: readonly RecordTab[];
  /**
   * Inhalt der Karte „Kennzahlen“ (z. B. `RecordMetricRows`). Gesetzt = eine Karte mit Umschalter „Kennzahlen · Verlauf“
   * im Kartenkopf, Kennzahlen offen ([RUDOLF 19.10.2026]); nicht gesetzt = nur die Karte „Verlauf“ (Belege, [RUDOLF 24.10.2026]).
   */
  figures?: ReactNode;
  /** KI-Hinweis-Karte (`RecordAiHint`, `AiCard`) unter der Karte; nur Vorschlag (AD-16). */
  hint?: ReactNode;
  /** Verlauf des Datensatzes; `null` = kein Verlauf (kein Recht, lädt) – dann keine Verlauf-Karte und kein Umschalter. */
  history?: RecordHistory | null;
  /**
   * Vorschau als eigene Karte über der Seitenleiste (EXPERIENCE §4.1 „rechts Belegvorschau …, darunter der Verlauf in der
   * Seitenleiste“): Belegvorschau am Auftrag und an der Bestellung, Prüf-Vorschau an der Retoure. Nie Teil des Umschalters.
   */
  preview?: ReactNode;
  /** Name der Reiterleiste für Hilfsmittel. */
  tabsLabel?: string;
  /** Name des ersten Reiters, Standard „Übersicht“. */
  overviewLabel?: string;
  /**
   * Reiterleiste weglassen, solange sie nur einen Reiter hätte (Ticket 14.18, Design clean: ein einzelner Reiter ist Deko
   * ohne Aussage; Ticket 1.302). Der Inhalt steht dann als Region ohne Leiste. `false` erzwingt die Leiste.
   */
  hideSingleTab?: boolean;
  /**
   * Gewählter Reiter von außen (Ticket 1.207, z. B. „Adresse bearbeiten“ im Kopf springt zur Übersicht); `'overview'` ist
   * die Übersicht. Ohne Angabe merkt sich der Rahmen den Reiter selbst.
   */
  tab?: string;
  /** Meldet jeden Reiterwechsel (zusammen mit `tab`). */
  onTabChange?: (tab: string) => void;
  /** Gewählter Teil der Seitenleiste von außen (`'kennzahlen'` oder `'verlauf'`); unbekannter Wert = Kennzahlen. */
  sidePart?: string;
  /** Meldet jeden Wechsel des Teils (z. B. zum Merken je Person). */
  onSidePart?: (value: string) => void;
}

/** Reiter-Zustand: von außen (`tab`/`onTabChange`) oder intern. */
function useChosenTab(tab: string | undefined, onTabChange: ((tab: string) => void) | undefined): [string, (value: string) => void] {
  const [own, setOwn] = useState<string>(OVERVIEW);
  const set = (value: string) => {
    setOwn(value);
    onTabChange?.(value);
  };
  return [tab ?? own, set];
}

/**
 * Ziel für die Handy-Knopfleiste des `RecordHeader` (Ticket 1.204, `PhoneActionBar`): `RecordLayout` stellt am Ende der Seite einen Platz
 * bereit; der Kopf hängt seine `FixedActionBar` dorthin (Portal), damit ihr Platzhalter unter dem letzten Abschnitt steht
 * und keine Lücke unter dem Kopf entsteht. Außerhalb: `null`.
 */
export const RecordActionSlot = createContext<HTMLElement | null>(null);
const TAB_BUTTON = 'flex min-h-8 cursor-pointer items-center gap-space-2 rounded-full px-space-4 desktop:px-space-3 text-md whitespace-nowrap pointer-coarse:min-h-11';

/**
 * Datensatz-Rahmen nach Datensatz-Muster (DESIGN „Datensatz-Kopf“, „Rechte Seitenleiste“; EXPERIENCE §10.2; Tickets 1.204,
 * 1.312) – ein Baustein für alle Datensatz-Arten.
 *
 * Desktop-Vollansicht: Kopf über die volle Breite; darunter links die Reiterleiste (Karte, Reiter dunkelblau über
 * `tabStateClass`) mit dem aktiven Reiter, rechts die Seitenleiste (`{spacing.record-side}`): Vorschau-Karte (falls
 * gesetzt), dann eine Karte mit Umschalter „Kennzahlen · Verlauf“ (ohne Kennzahlen nur „Verlauf“), darunter der KI-Hinweis.
 * Nicht gewählte Reiter und Teile bleiben eingehängt (`hidden`), Eingaben gehen nicht verloren.
 *
 * Tablet, Handy, Seitenpanel: alles untereinander – Kopf, Sprung-Reiter (nur die Reiter, kein „Verlauf“), alle
 * Reiter-Inhalte, dann die Seitenleiste mit demselben Umschalter. Tippen springt zum Abschnitt und setzt den Fokus dorthin;
 * Pfeiltasten wechseln zwischen den Sprung-Reitern. Beide Darstellungen haben denselben Baum (nur Klassen und Attribute
 * wechseln), ein Größenwechsel baut nichts neu auf.
 */
export function RecordLayout({
  variant = 'page',
  head,
  overview,
  primaryAction,
  tabs = [],
  figures,
  hint,
  history = null,
  preview,
  tabsLabel = 'Bereiche des Datensatzes',
  overviewLabel = 'Übersicht',
  hideSingleTab = true,
  tab: tabFromOutside,
  onTabChange,
  sidePart,
  onSidePart,
}: RecordLayoutProps) {
  const screen = useScreen();
  const phone = screen === 'phone';
  const withFigures = figures !== undefined && figures !== null && figures !== false;
  // Teile der Seitenleiste: Kennzahlen (falls beschrieben) und Verlauf (falls erlaubt); Umschalter erst ab zwei Teilen.
  const sideValues: readonly { value: string; label: string }[] = [
    ...(withFigures ? [{ value: KENNZAHLEN, label: 'Kennzahlen' }] : []),
    ...(history ? [{ value: VERLAUF, label: 'Verlauf' }] : []),
  ];
  const toggle = sideValues.length > 1;
  // Gewählter Teil: von außen oder intern; unbekannt (z. B. alter gemerkter Wert „belegvorschau“) = erster Teil.
  // Sprung auf einen Verlaufseintrag (`#eintrag-…`) öffnet den Teil „Verlauf“; die eigene Wahl geht vor `sidePart`, damit ein Klick wirkt.
  const [ownSide, setOwnSide] = useState<string | null>(() =>
    history && typeof window !== 'undefined' && window.location.hash.startsWith('#eintrag-') ? VERLAUF : null,
  );
  const wanted = ownSide ?? sidePart ?? sideValues[0]?.value ?? VERLAUF;
  const side = sideValues.some((v) => v.value === wanted) ? wanted : (sideValues[0]?.value ?? VERLAUF);
  const setSide = (value: string) => {
    setOwnSide(value);
    onSidePart?.(value);
  };
  const desktop = screen === 'desktop' && variant === 'page';
  const [chosen, setChosen] = useChosenTab(tabFromOutside, onTabChange);
  const id = useId();
  const nav = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);
  // Fokus vor einem Umschalter-Wechsel (siehe chooseSide); null, wenn kein Wechsel aussteht.
  const pendingFocus = useRef<{ before: Element | null; value: string } | null>(null);
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);
  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
  }, []);

  const shown: readonly { value: string; label: string; count?: number | undefined }[] = [
    { value: OVERVIEW, label: overviewLabel },
    ...tabs.map((t) => ({ value: t.value, label: t.label, count: t.count })),
  ];
  const active = shown.some((t) => t.value === chosen) ? chosen : OVERVIEW;
  const tabId = (t: string) => `${id}-reiter-${t}`;
  const panelId = (t: string) => `${id}-inhalt-${t}`;

  // Sprung-Reiter: Abschnitt in den Blick holen; Zeiger setzt den Fokus auf den Abschnitt (nur wenn ihn nichts anderes
  // inzwischen bekommen hat), die Tastatur lässt ihn auf dem Reiter.
  function jump(value: string, by: 'pointer' | 'keyboard') {
    setChosen(value);
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    const before = document.activeElement;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const focusStillHere = document.activeElement === before;
      const target = document.getElementById(panelId(value));
      if (by === 'pointer') {
        target?.scrollIntoView({ block: 'start', inline: 'nearest' });
        if (focusStillHere) target?.focus({ preventScroll: true });
        return;
      }
      const tab = document.getElementById(tabId(value));
      if (focusStillHere) tab?.focus();
      const bar = nav.current;
      if (tab && bar) bar.scrollTo({ left: bar.scrollLeft + tab.getBoundingClientRect().left - bar.getBoundingClientRect().left });
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const focused = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-record-tab]');
    if (!focused) return;
    const i = shown.findIndex((t) => t.value === focused.dataset.recordTab);
    if (i < 0) return;
    let next: number;
    if (e.key === 'ArrowRight') next = (i + 1) % shown.length;
    else if (e.key === 'ArrowLeft') next = (i + shown.length - 1) % shown.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = shown.length - 1;
    else return;
    e.preventDefault();
    const value = shown[next]!.value;
    if (desktop) {
      setChosen(value);
      requestAnimationFrame(() => document.getElementById(tabId(value))?.focus());
    } else jump(value, 'keyboard');
  }

  // Reiter-Inhalt: am Desktop Reiter-Fläche (nur der aktive sichtbar), sonst Sprungziel (alle sichtbar).
  const tabBar = !(hideSingleTab && shown.length < 2);
  const section = (t: string, label: string) =>
    desktop && tabBar
      ? { id: panelId(t), role: 'tabpanel' as const, 'aria-labelledby': tabId(t), hidden: active !== t, tabIndex: -1 }
      : { id: panelId(t), role: 'region' as const, 'aria-label': label, tabIndex: -1 };
  const sectionClass = 'flex min-w-0 scroll-mt-24 flex-col gap-card-gap outline-none';

  // Umschalter der Seitenleiste („Kennzahlen · Verlauf“): sitzt im Kopf der gerade sichtbaren Karte, nie frei darüber.
  // Klick und Pfeiltasten (←/→, Pos1/Ende) wechseln; der Fokus wandert mit in den Kopf der neuen Karte.
  const sideButtonId = (value: string) => `${id}-umschalter-${value}`;
  // Der Fokus folgt dem Knopf in den Kopf der anderen Karte – aber nur, wenn er noch auf dem Element steht, das vor dem
  // Umschalten fokussiert war, oder mit dem umgehängten Knopf verloren ging (Wackel-Test auftrag-platzsparend, main
  // 22.10.2026). Gleich nach dem Umhängen (Layout-Effekt), damit eine schnell folgende Taste (Pos1, dann ←) nicht ins
  // Leere geht; das nächste Bild bleibt als Rückfall, falls der Knopf erst später erscheint.
  const followFocus = (pending: { before: Element | null; value: string }) => {
    const now = document.activeElement;
    if (now === pending.before || now === null || now === document.body) document.getElementById(sideButtonId(pending.value))?.focus();
  };
  const chooseSide = (value: string) => {
    const pending = { before: document.activeElement, value };
    pendingFocus.current = pending;
    setSide(value);
    requestAnimationFrame(() => {
      if (pendingFocus.current === pending) pendingFocus.current = null;
      followFocus(pending);
    });
  };
  useLayoutEffect(() => {
    const pending = pendingFocus.current;
    if (!pending || pending.value !== side) return;
    pendingFocus.current = null;
    followFocus(pending);
  });
  function onSideKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Vom fokussierten Knopf aus zählen (Tab auf einen anderen Knopf, dann →); sonst vom aktiven Teil.
    const focused = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-side-part]')?.dataset.sidePart;
    const at = sideValues.findIndex((v) => v.value === focused);
    const i = at >= 0 ? at : sideValues.findIndex((v) => v.value === side);
    let next: number;
    if (e.key === 'ArrowRight') next = (i + 1) % sideValues.length;
    else if (e.key === 'ArrowLeft') next = (i + sideValues.length - 1) % sideValues.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = sideValues.length - 1;
    else return;
    e.preventDefault();
    chooseSide(sideValues[next]!.value);
  }
  const sideTabs = toggle ? (
    <div role="group" aria-label="Seitenleiste umschalten" data-testid="seitenleiste-umschalter" onKeyDown={onSideKeyDown} className={SEG_TRACK}>
      {sideValues.map(({ value, label }) => (
        <button
          key={value}
          id={sideButtonId(value)}
          data-side-part={value}
          type="button"
          aria-pressed={side === value}
          onClick={() => chooseSide(value)}
          className={cn(TAB_BUTTON, 'pointer-coarse:min-w-11', tabStateClass(side === value, { seg: true }))}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <RecordActionSlot.Provider value={actionSlot}>
      <div data-testid="datensatz-muster" data-darstellung={desktop ? 'spalten' : 'untereinander'} className="flex min-w-0 flex-col gap-card-gap">
        <UnsavedBar phone={phone} />
        {head}
        {primaryAction && !phone ? primaryAction : null}
        <div className={desktop ? 'grid grid-cols-[minmax(0,1fr)_var(--record-side)] items-start gap-card-gap' : 'flex min-w-0 flex-col gap-card-gap'}>
          <div className={desktop ? 'flex min-w-0 flex-col gap-card-gap' : 'contents'}>
            {/* Reiter immer in einer Reiterkarte (Rudolf 13.10.2026; nie frei auf dem Grund, [RUDOLF 19.10.2026]), waagerecht wischbar; Rand-Verlauf, wo noch Reiter folgen (Story 12.6). */}
            {!tabBar ? null : (
              <TabCard ref={nav} testId="reiterleiste" edgeFade active={desktop ? String(active) : undefined}>
                <div role={desktop ? 'tablist' : 'group'} aria-label={tabsLabel} onKeyDown={onKeyDown} className={TAB_TRACK}>
                  {shown.map((t) => {
                    const on = active === t.value;
                    return (
                      <button
                        key={t.value}
                        id={tabId(t.value)}
                        data-record-tab={t.value}
                        type="button"
                        role={desktop ? 'tab' : undefined}
                        aria-selected={desktop ? on : undefined}
                        aria-pressed={desktop ? undefined : on}
                        aria-controls={panelId(t.value)}
                        tabIndex={desktop ? (on ? 0 : -1) : undefined}
                        onClick={() => (desktop ? setChosen(t.value) : jump(t.value, 'pointer'))}
                        className={cn(TAB_BUTTON, 'pointer-coarse:min-w-11', tabStateClass(on))}
                      >
                        {t.label}
                        {t.count !== undefined ? <span className={cn('tabular-nums', on ? TAB_COUNT_ACTIVE : TAB_COUNT_INACTIVE)}>{t.count}</span> : null}
                      </button>
                    );
                  })}
                </div>
              </TabCard>
            )}
            <div {...section(OVERVIEW, overviewLabel)} className={sectionClass}>
              {overview}
            </div>
            {tabs.map((t) => (
              <div key={t.value} {...section(t.value, t.label)} className={sectionClass}>
                {t.content}
              </div>
            ))}
          </div>
          <RecordSidebar
            preview={preview}
            figures={withFigures ? figures : null}
            history={history}
            hint={hint}
            side={side}
            sideTabs={sideTabs}
          />
        </div>
        {primaryAction && phone ? <FixedActionBar>{primaryAction}</FixedActionBar> : null}
        {/* Platz für die Handy-Knopfleiste des Kopfs (`RecordActionSlot`). */}
        <div ref={setActionSlot} className="contents" />
      </div>
    </RecordActionSlot.Provider>
  );
}

/**
 * Unten fixierte Knopfleiste am Handy: misst ihre Höhe (ResizeObserver) und hält darüber einen gleich hohen Platzhalter,
 * damit kein Inhalt verdeckt bleibt. Die Höhe steht zusätzlich als `--fixed-bar-h` am Dokument (Rückgängig-Einblendung
 * steht darüber, EXPERIENCE §10.2); beim Abbau wird sie entfernt.
 */
export function FixedActionBar({
  children,
  framed = false,
}: {
  children: ReactNode;
  /** Opt-in (Ticket 1.273): eigene Fläche mit Rahmenlinie oben und Polster für den Geräte-Rand unten (Safe Area). */
  framed?: boolean;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = bar.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      setHeight(h);
      root.style.setProperty('--fixed-bar-h', `${h}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--fixed-bar-h');
    };
  }, []);
  return (
    <>
      <div aria-hidden data-testid="knopfleiste-platzhalter" style={{ height }} />
      <div ref={bar} data-testid="knopfleiste" className={cn('fixed inset-x-0 bottom-0 z-30 bg-[var(--glass)] px-space-4 py-space-3 backdrop-blur-[16px]', framed && 'border-t border-line pb-[max(var(--spacing-space-3),env(safe-area-inset-bottom))]')}>
        {children}
      </div>
    </>
  );
}
